import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  deletePid,
  downloadPidPdf,
  getComponentHierarchy,
  getPids,
  getSystems,
  lockAllPids,
  lockAllRequiredSpofPids,
  lockPid,
  lockRequiredSpofPid,
  renamePid,
  retryPid,
  updatePidSubSystem,
  uploadPids,
} from "../api";
import { triggerDownload } from "../utils/download";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import PidContextMenu from "./PidContextMenu";
import {
  OPENABLE_STATUSES,
  StatusChip,
  StatusChipFromSummary,
} from "./StatusChip";

interface Props {
  powerPlantId: string;
  systemId: string;
  onSelectPid?: (pidId: string) => void;
  onSelectPidWithComments?: (pidId: string) => void;
  isAdmin: boolean;
}

function filterPdfs(files: File[]): File[] {
  return files.filter((f) => f.type === "application/pdf");
}

function sortByName(files: File[]): File[] {
  return [...files].sort((a, b) => a.name.localeCompare(b.name));
}

export default function PidList({
  powerPlantId,
  systemId,
  onSelectPid,
  onSelectPidWithComments,
  isAdmin,
}: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [duplicateNames, setDuplicateNames] = useState<string[]>([]);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState("");
  const [pidMenuAnchor, setPidMenuAnchor] = useState<null | {
    id: string;
    el: HTMLElement;
  }>(null);
  const [reprocessId, setReprocessId] = useState<string | null>(null);

  const {
    data: pids = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["pids", powerPlantId, systemId],
    queryFn: () => getPids(powerPlantId, systemId),
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.some(
        (p) => p.status === "processing" || p.status === "required_spof_review",
      );
      return hasProcessing ? 5_000 : 30_000;
    },
  });

  const { data: systems = [] } = useQuery({
    queryKey: ["systems", powerPlantId],
    queryFn: () => getSystems(powerPlantId),
  });

  const { data: componentHierarchy } = useQuery({
    queryKey: ["componentHierarchy"],
    queryFn: getComponentHierarchy,
    staleTime: Infinity,
  });

  const systemType = systems.find((s) => s.id === systemId)?.system_type ?? "";
  const subSystemOptions =
    componentHierarchy && systemType && componentHierarchy[systemType]
      ? Object.keys(componentHierarchy[systemType])
      : [];

  const subSystemMut = useMutation({
    mutationFn: ({
      pidId,
      sub_system,
    }: {
      pidId: string;
      sub_system: string;
    }) => updatePidSubSystem(powerPlantId, systemId, pidId, sub_system),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
    },
  });

  const invalidatePidQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["pids", powerPlantId, systemId],
    });
    queryClient.invalidateQueries({ queryKey: ["systems", powerPlantId] });
    queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
  };

  const uploadMut = useMutation({
    mutationFn: (files: File[]) => uploadPids(powerPlantId, systemId, files),
    onSuccess: invalidatePidQueries,
  });

  const deleteMut = useMutation({
    mutationFn: (pidId: string) => deletePid(powerPlantId, systemId, pidId),
    onSuccess: () => {
      invalidatePidQueries();
      setDeleteId(null);
    },
  });

  const renameMut = useMutation({
    mutationFn: ({ pidId, filename }: { pidId: string; filename: string }) =>
      renamePid(powerPlantId, systemId, pidId, filename),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
      setRenameId(null);
      setRenameName("");
    },
  });

  const lockMut = useMutation({
    mutationFn: ({ pidId, locked }: { pidId: string; locked: boolean }) =>
      lockPid(powerPlantId, systemId, pidId, locked),
    onSuccess: invalidatePidQueries,
  });

  const lockAllMut = useMutation({
    mutationFn: (locked: boolean) =>
      lockAllPids(powerPlantId, systemId, locked),
    onSuccess: invalidatePidQueries,
  });

  const lockRequiredSpofMut = useMutation({
    mutationFn: ({ pidId, locked }: { pidId: string; locked: boolean }) =>
      lockRequiredSpofPid(powerPlantId, systemId, pidId, locked),
    onSuccess: invalidatePidQueries,
  });

  const lockAllRequiredSpofMut = useMutation({
    mutationFn: (locked: boolean) =>
      lockAllRequiredSpofPids(powerPlantId, systemId, locked),
    onSuccess: invalidatePidQueries,
  });

  const retryMut = useMutation({
    mutationFn: (pidId: string) => retryPid(powerPlantId, systemId, pidId),
    onSuccess: () => {
      invalidatePidQueries();
      setReprocessId(null);
    },
  });

  const handleDownload = async (pidId: string, filename: string) => {
    const blob = await downloadPidPdf(powerPlantId, systemId, pidId);
    triggerDownload(
      blob,
      filename.toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`,
    );
  };

  const totalKks = pids.reduce((sum, p) => sum + (p.kks?.length ?? 0), 0);

  // Derived lock state
  const allApproved =
    pids.length > 0 && pids.every((p) => p.status === "approved");
  const anyLocked = pids.some((p) => p.locked);
  const allLocked = pids.length > 0 && pids.every((p) => p.locked);

  // Derived required-SPOF lock state
  const allPastProcessing =
    pids.length > 0 &&
    pids.every((p) => p.status !== "processing" && p.status !== "error");
  const anyRequiredSpofLocked = pids.some((p) => p.required_spof_locked);
  const allRequiredSpofLocked =
    pids.length > 0 && pids.every((p) => p.required_spof_locked);

  const processFiles = useCallback(
    (rawFiles: File[]) => {
      const pdfs = sortByName(filterPdfs(rawFiles));
      const existingNames = new Set(pids.map((p) => p.filename));
      const accepted: File[] = [];
      const discarded: string[] = [];
      for (const f of pdfs) {
        if (existingNames.has(f.name)) {
          discarded.push(f.name);
        } else {
          accepted.push(f);
        }
      }
      if (discarded.length > 0) setDuplicateNames(discarded);
      if (accepted.length > 0) uploadMut.mutate(accepted);
    },
    [pids, uploadMut],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
      e.target.value = "";
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (!isAdmin) return;
      processFiles(Array.from(e.dataTransfer.files));
    },
    [processFiles, isAdmin],
  );
  const menuPid = pids.find((p) => p.id === pidMenuAnchor?.id);
  const reprocessPid = pids.find((p) => p.id === reprocessId);
  const reprocessKksCount = reprocessPid?.kks?.length ?? 0;

  useEffect(() => {
    if (!pidMenuAnchor) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      const menu = document.querySelector('[role="menu"]');
      if (menu?.contains(e.target as Node)) return;
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const pidEl = elements.find(
        (el) => el instanceof HTMLElement && (el as HTMLElement).dataset.pidId,
      ) as HTMLElement | undefined;
      if (pidEl?.dataset.pidId) {
        setPidMenuAnchor({ id: pidEl.dataset.pidId, el: pidEl });
      } else {
        setPidMenuAnchor(null);
      }
    };
    document.addEventListener("contextmenu", handler, true);
    return () => document.removeEventListener("contextmenu", handler, true);
  }, [pidMenuAnchor]);

  return (
    <Box
      sx={{
        borderRadius: 0,
        bgcolor: dragOver ? "rgba(0,157,224,0.04)" : "transparent",
        transition: "background-color 0.2s",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            fontWeight: 700,
            color: "#002A5C",
            fontSize: "0.85rem",
          }}
        >
          PIDs ({pids.length} file{pids.length !== 1 ? "s" : ""}, {totalKks}{" "}
          KKS)
        </Typography>
        {isAdmin && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              hidden
              onChange={handleFileChange}
            />
            <Tooltip
              title={
                pids.length === 0
                  ? "No PIDs to required-SPOF-lock"
                  : !allPastProcessing
                    ? "All PIDs must have completed OCR before required-SPOF-locking"
                    : allRequiredSpofLocked
                      ? "Unlock required SPOF on all PIDs"
                      : "Lock required SPOF on all eligible PIDs"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={
                    pids.length === 0 ||
                    (!allPastProcessing && !anyRequiredSpofLocked) ||
                    lockAllRequiredSpofMut.isPending
                  }
                  onClick={() =>
                    lockAllRequiredSpofMut.mutate(
                      allRequiredSpofLocked ? false : true,
                    )
                  }
                  sx={{
                    color:
                      pids.length === 0 || !allPastProcessing
                        ? "#C4CDD8"
                        : allRequiredSpofLocked
                          ? "#6A1B9A"
                          : "#AB47BC",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                    "&.Mui-disabled": { color: "#C4CDD8" },
                  }}
                >
                  {allRequiredSpofLocked ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    <LockOpenIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                pids.length === 0
                  ? "No PIDs to lock"
                  : !allApproved
                    ? "All PIDs must be approved before locking"
                    : allLocked
                      ? "Unlock all PIDs"
                      : "Lock all approved PIDs"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={
                    pids.length === 0 ||
                    (!allApproved && !anyLocked) ||
                    lockAllMut.isPending
                  }
                  onClick={() => lockAllMut.mutate(allLocked ? false : true)}
                  sx={{
                    color:
                      pids.length === 0 || !allApproved
                        ? "#C4CDD8"
                        : allLocked
                          ? "#E30613"
                          : "#43A047",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.04)",
                    },
                    "&.Mui-disabled": { color: "#C4CDD8" },
                  }}
                >
                  {allLocked ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    <LockOpenIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMut.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "#009DE0",
                borderColor: "#009DE0",
                "&:hover": {
                  bgcolor: "rgba(0,157,224,0.06)",
                  borderColor: "#0087C3",
                },
                borderRadius: 2,
                px: 1.5,
              }}
            >
              Upload PDFs
            </Button>
          </>
        )}
      </Box>

      {(isLoading || uploadMut.isPending) && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}

      {isError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Failed to load PIDs.
        </Typography>
      )}
      {uploadMut.isError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Upload failed. Please try again.
        </Typography>
      )}

      {/* Empty state */}
      {pids.length === 0 && !isLoading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: "2px dashed #DEE3EA",
            borderRadius: 3,
            py: 6,
            color: "#8E99A8",
            gap: 1,
          }}
        >
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 48, color: "#C4CDD8" }}
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#8E99A8" }}
          >
            No PDFs uploaded yet
          </Typography>
          <Typography variant="caption" sx={{ color: "#B0BAC5" }}>
            Drag &amp; drop PDF files here, or click "Upload PDFs"
          </Typography>
        </Box>
      )}

      {/* PID list items */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 0.5 }}>
        <List dense disablePadding>
          {pids.map((pid) => {
            const openable = OPENABLE_STATUSES.has(pid.status);
            return (
              <ListItem key={pid.id} disableGutters disablePadding>
                <ListItemButton
                  data-pid-id={pid.id}
                  onClick={() => {
                    if (openable && onSelectPid) onSelectPid(pid.id);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPidMenuAnchor({ id: pid.id, el: e.currentTarget });
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    minHeight: 45,
                    borderRadius: 1.5,
                    cursor: openable ? "pointer" : "default",
                    "&:hover": openable
                      ? { bgcolor: "#EDF1F7" }
                      : { bgcolor: "transparent" },
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1,
                      bgcolor: "#FDE8E8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mr: 1.5,
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 16, color: "#e53935" }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        color: "#002A5C",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pid.filename}
                    </Typography>
                  </Box>
                  {/* 4-column stats area: [bubble icon] [comment count] [KKS icon] [KKS count] */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "12px 20px 16px 24px",
                      alignItems: "center",
                      columnGap: "4px",
                      flexShrink: 0,
                      mr: 0.5,
                      color: "#6B7A90",
                      fontSize: "0.7rem",
                    }}
                  >
                    {/* Col 1-2: comment bubble icon and count (larger clickable zone) */}
                    {pid.comment_count > 0 ? (
                      <Tooltip
                        title={`${pid.comment_count} comment${pid.comment_count !== 1 ? "s" : ""} — click to view`}
                      >
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onSelectPidWithComments)
                              onSelectPidWithComments(pid.id);
                            else if (onSelectPid) onSelectPid(pid.id);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            gridColumn: "span 2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            color: "#5C6B7A",
                            cursor: "pointer",
                            borderRadius: "4px",
                            padding: "2px 4px",
                            "&:hover": {
                              color: "#009DE0",
                              bgcolor: "rgba(0, 157, 224, 0.08)",
                            },
                          }}
                        >
                          <ChatBubbleOutlineIcon sx={{ fontSize: 12 }} />
                          <Box>{pid.comment_count}</Box>
                        </Box>
                      </Tooltip>
                    ) : (
                      <>
                        {/* Empty placeholders when no comments */}
                        <Box />
                        <Box />
                      </>
                    )}
                    {/* Col 3: KKS icon */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <svg
                        width="16"
                        height="12"
                        viewBox="0 0 16 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ display: "block" }}
                      >
                        <rect
                          x="0.5"
                          y="0.5"
                          width="15"
                          height="11"
                          rx="1.5"
                          stroke="currentColor"
                          strokeWidth="1"
                          fill="none"
                        />
                        <text
                          x="8"
                          y="9"
                          textAnchor="middle"
                          fontSize="6"
                          fontWeight="700"
                          fill="currentColor"
                          fontFamily="inherit"
                          letterSpacing="0.3"
                        >
                          KKS
                        </text>
                      </svg>
                    </Box>
                    {/* Col 4: KKS count */}
                    <Box sx={{ textAlign: "right" }}>
                      {pid.kks?.length ?? 0}
                    </Box>
                  </Box>
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ width: 180, flexShrink: 0, mx: 1 }}
                  >
                    {(() => {
                      const hasComponentAssigned = !!pid.kks?.some(
                        (k) => k.component && k.component !== "Unknown",
                      );
                      return (
                        <Tooltip
                          title={
                            hasComponentAssigned
                              ? "Sub-system cannot be changed once components have been assigned to KKS items"
                              : ""
                          }
                          placement="top"
                        >
                          <span>
                            <Autocomplete
                              size="small"
                              options={subSystemOptions}
                              value={
                                pid.sub_system === "Unknown"
                                  ? null
                                  : pid.sub_system
                              }
                              disabled={hasComponentAssigned}
                              onChange={(_e, v) => {
                                subSystemMut.mutate({
                                  pidId: pid.id,
                                  sub_system: v ?? "Unknown",
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Sub system…"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      fontSize: "0.72rem",
                                      py: 0,
                                    },
                                  }}
                                />
                              )}
                              disableClearable={false}
                            />
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  {/* Status chip */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      ml: 1,
                      width: 130,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {pid.status === "processing" &&
                    pid.total_pages != null &&
                    pid.total_pages > 0 ? (
                      <StatusChipFromSummary
                        status="processing"
                        percentage={Math.round(
                          ((pid.pages_processed ?? 0) / pid.total_pages) * 100,
                        )}
                      />
                    ) : (
                      <StatusChip status={pid.status} />
                    )}
                  </Box>
                  {/* Lock icons */}
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isAdmin ? (
                      <>
                        <Tooltip
                          title={
                            pid.status === "processing" ||
                            pid.status === "error"
                              ? "PID must have completed OCR before required-SPOF-locking"
                              : pid.required_spof_locked
                                ? "Unlock required SPOF"
                                : "Lock required SPOF"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                pid.status === "processing" ||
                                pid.status === "error" ||
                                lockRequiredSpofMut.isPending
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                lockRequiredSpofMut.mutate({
                                  pidId: pid.id,
                                  locked: !pid.required_spof_locked,
                                });
                              }}
                              sx={{
                                color:
                                  pid.status === "processing" ||
                                  pid.status === "error"
                                    ? "#C4CDD8"
                                    : pid.required_spof_locked
                                      ? "#6A1B9A"
                                      : "#AB47BC",
                                "&.Mui-disabled": { color: "#C4CDD8" },
                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              {pid.required_spof_locked ? (
                                <LockIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <LockOpenIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            pid.status !== "approved"
                              ? "PID must be approved before it can be locked"
                              : pid.locked
                                ? "Unlock this PID"
                                : "Lock this PID"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                pid.status !== "approved" || lockMut.isPending
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                lockMut.mutate({
                                  pidId: pid.id,
                                  locked: !pid.locked,
                                });
                              }}
                              sx={{
                                color:
                                  pid.status !== "approved"
                                    ? "#C4CDD8"
                                    : pid.locked
                                      ? "#E30613"
                                      : "#43A047",
                                "&.Mui-disabled": { color: "#C4CDD8" },
                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              {pid.locked ? (
                                <LockIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <LockOpenIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip
                        title={
                          pid.status !== "approved"
                            ? "PID must be approved before it can be locked"
                            : pid.locked
                              ? "Locked — only an admin can unlock"
                              : "Unlocked"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disableRipple
                            sx={{
                              pointerEvents: "none",
                              color:
                                pid.status !== "approved"
                                  ? "#C4CDD8"
                                  : pid.locked
                                    ? "#E30613"
                                    : "#43A047",
                            }}
                          >
                            {pid.locked ? (
                              <LockIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <LockOpenIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPidMenuAnchor({ id: pid.id, el: e.currentTarget });
                      }}
                      sx={{
                        color: "#8E99A8",
                        p: 0.25,
                        "&:hover": { color: "#009DE0" },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Context menu */}
      <PidContextMenu
        anchorEl={pidMenuAnchor?.el ?? null}
        open={Boolean(pidMenuAnchor)}
        pid={menuPid}
        isAdmin={isAdmin}
        onClose={() => setPidMenuAnchor(null)}
        onDownload={() => {
          if (pidMenuAnchor && menuPid)
            handleDownload(pidMenuAnchor.id, menuPid.filename);
        }}
        onReprocess={() => {
          if (pidMenuAnchor && menuPid) {
            const kksCount = menuPid.kks?.length ?? 0;
            if (kksCount > 0) {
              setReprocessId(pidMenuAnchor.id);
            } else {
              retryMut.mutate(pidMenuAnchor.id);
            }
          }
        }}
        onRename={() => {
          if (pidMenuAnchor && menuPid) {
            setRenameId(pidMenuAnchor.id);
            setRenameName(menuPid.filename);
          }
        }}
        onDelete={() => {
          if (pidMenuAnchor) setDeleteId(pidMenuAnchor.id);
        }}
      />
      {/* Reprocess confirmation dialog */}
      <Dialog
        open={reprocessId !== null}
        onClose={() => setReprocessId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Reprocess</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{reprocessPid?.filename}</strong> already has{" "}
            <strong>{reprocessKksCount} KKS</strong> extracted, including any
            user-encoded information. Reprocessing will permanently remove all
            of them.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Are you sure you want to reprocess this file?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReprocessId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={retryMut.isPending}
            onClick={() => {
              if (reprocessId) retryMut.mutate(reprocessId);
            }}
          >
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate dialog */}
      <Dialog
        open={duplicateNames.length > 0}
        onClose={() => setDuplicateNames([])}
      >
        <DialogTitle>Duplicate files skipped</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The following files already exist and were not uploaded:
          </Typography>
          <List dense disablePadding>
            {duplicateNames.map((name) => (
              <ListItem key={name} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PictureAsPdfIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary={name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateNames([])}>OK</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteId !== null}
        title="Delete PID"
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{pids.find((p) => p.id === deleteId)?.filename}</strong>?
            This cannot be undone.
          </>
        }
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        isPending={deleteMut.isPending}
      />

      {/* Rename dialog */}
      <Dialog
        open={renameId !== null}
        onClose={() => {
          setRenameId(null);
          setRenameName("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename PID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Filename"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameName.trim() && renameId) {
                renameMut.mutate({
                  pidId: renameId,
                  filename: renameName.trim(),
                });
              }
            }}
            sx={{ mt: 1 }}
            error={renameMut.isError}
            helperText={
              renameMut.isError
                ? "Rename failed. The name may already be in use."
                : undefined
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenameId(null);
              setRenameName("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!renameName.trim() || renameMut.isPending}
            onClick={() => {
              if (renameId && renameName.trim()) {
                renameMut.mutate({
                  pidId: renameId,
                  filename: renameName.trim(),
                });
              }
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
